import { styled } from '@mui/material/styles';
import {
  Autocomplete,
  Box,
  FormHelperText,
  TextField,
  createFilterOptions,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PhoneIcon from '@mui/icons-material/Phone';
import TextSearch from '../../controls/TextSearch';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Client } from '../../../data/models/client.model';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { isPhoneSearchLongEnough } from '../../../services/phoneUtils';

// Substring match anywhere in the client's legal name, not just at the start, and blind to case
// and accents - so "arzte" finds "Ärzte Süd". Only legalName is searched, because that is the text
// the option actually shows.
const filterClients = createFilterOptions<Client>({
  matchFrom: 'any',
  ignoreCase: true,
  ignoreAccents: true,
  trim: true,
  stringify: (client) => client.legalName ?? '',
});

const PREFIX = 'SearchToolbar';

const classes = {
  container: `${PREFIX}-container`,
  toolbar: `${PREFIX}-toolbar`,
  search: `${PREFIX}-search`,
  phoneSearch: `${PREFIX}-phoneSearch`,
  dropdown: `${PREFIX}-dropdown`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(3),
    gap: theme.spacing(1),
  },

  [`& .${classes.toolbar}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },

  [`& .${classes.search}`]: {
    flexGrow: 1,
    minWidth: 300,
    maxWidth: 450,
  },

  [`& .${classes.phoneSearch}`]: {
    minWidth: 165,
    maxWidth: 210,
  },

  [`& .${classes.dropdown}`]: {
    minWidth: 300,
    backgroundColor: '#fff',
  },
}));

interface SearchToolbarProps {
  clients: Client[];
  currentClientBC: string;
  currentSearchText: string;
  currentPhoneSearch: string;
  isPortal: boolean;
  /**
   * Whether to offer the phone lookup at all. The parent decides: it is for an operator in the
   * Amazon Connect workspace, so the field only exists where the app is framed by it.
   */
  showPhoneSearch: boolean;
  /**
   * Changes whenever the search was replaced from outside this toolbar - an incoming call in the
   * Amazon Connect workspace. It is part of the input keys, because TextSearch holds its value in
   * local state and stops following currentSearchFilter after mount, so remounting is the only way
   * to get a new value into the box.
   */
  searchResetToken?: number;
  onClientChange: (val: string | null) => void;
  onSearchTextChange: (val: string) => void;
  onPhoneSearchChange: (val: string) => void;
  // onCreatePassenger: () => void;
}

export default function SearchToolbar({
  clients,
  currentClientBC,
  currentSearchText,
  currentPhoneSearch,
  isPortal,
  showPhoneSearch,
  searchResetToken = 0,
  onClientChange,
  onSearchTextChange,
  onPhoneSearchChange,
}: SearchToolbarProps) {
  const t = useCommonTranslation();

  // The API rejects a client-less lookup below the digit floor, and the parent does not send one.
  // Saying so is left to this hint, because the field's own placeholder is never displayed - see
  // TextSearch, which shows the label there instead.
  const phoneSearchTooShort =
    !!currentPhoneSearch && !isPhoneSearchLongEnough(currentPhoneSearch);

  // The parent works in clientBC strings; Autocomplete works in option objects.
  const selectedClient =
    clients?.find((client) => client.clientBC === currentClientBC) ?? null;

  return (
    <StyledBox className={classes.container}>
      <LocalizationProvider dateAdapter={AdapterLuxon}>
        <Box className={classes.toolbar}>
          {!isPortal && (
            <Autocomplete
              className={classes.dropdown}
              options={clients ?? []}
              value={selectedClient}
              onChange={(_evt, client) =>
                onClientChange(client ? client.clientBC : null)
              }
              getOptionLabel={(client) => client.legalName ?? ''}
              getOptionKey={(client) => client.clientBC}
              isOptionEqualToValue={(a, b) => a.clientBC === b.clientBC}
              filterOptions={filterClients}
              autoHighlight
              openOnFocus
              noOptionsText={t('passenger.noClientsMatch')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('passenger.selectClient')}
                  variant="outlined"
                  required={true}
                />
              )}
            />
          )}
          <Box className={classes.search}>
            <TextSearch
              key={`text-${searchResetToken}`}
              id="text-search"
              icon={<SearchIcon />}
              label={t('passenger.search')}
              placeholder={t('passenger.searchPlaceholder')}
              currentSearchFilter={currentSearchText}
              disabled={!currentClientBC || !!currentPhoneSearch}
              onSearchChange={onSearchTextChange}></TextSearch>
          </Box>
          {/*
            Phone search. It spans all clients - an operator taking a call in the Amazon Connect
            workspace has the caller's number and nothing else - and the API rejects a phone term
            combined with a client, so the two are mutually exclusive: picking a client turns this
            field off, clearing the client turns it back on. Whether it is offered at all is the
            parent's decision; see showPhoneSearch.

            The key remounts the field when the client selection appears or disappears. TextSearch
            holds its value in local state and stops following currentSearchFilter after mount, so
            remounting is the only way the parent can get the old number out of the box.
          */}
          {showPhoneSearch && (
            <Box className={classes.phoneSearch}>
              <TextSearch
                key={`phone-${currentClientBC ? 'client-selected' : 'no-client'}-${searchResetToken}`}
                id="phone-search"
                icon={<PhoneIcon />}
                label={t('passenger.phoneSearch')}
                placeholder={t('passenger.phoneSearchPlaceholder')}
                currentSearchFilter={currentPhoneSearch}
                disabled={!!currentClientBC}
                onSearchChange={onPhoneSearchChange}></TextSearch>
              {phoneSearchTooShort && (
                <FormHelperText>
                  {t('passenger.phoneSearchTooShort')}
                </FormHelperText>
              )}
            </Box>
          )}
        </Box>
      </LocalizationProvider>
    </StyledBox>
  );
}
